import { ExportOutlined } from "@ant-design/icons";
import {
  CLOUD_MISMATCH,
  type RepositoryAccessConfig,
} from "@p0security/integrations-github-shared";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  Collapse,
  CommandDisplay,
  Flex,
  Typography,
} from "@p0security/ui";
import type { ReactNode } from "react";

import { toAppCreationUrl, toOrgAppsUrl } from "./app-registration.ts";
import { CLOUDS } from "./clouds.ts";
import type { RenderedCommands } from "./commands.ts";
import { InstallSteps, PROPAGATION_NOTE } from "./install-steps.tsx";

const { Text } = Typography;

/** Whether P0 validated the stored item with its connector. P0 calls the
 * connector once the item names its app, which the customer enters on the
 * configure step. Pass it the item P0 stored, never a form's draft: a draft
 * has the App ID before P0 has checked it. */
export const verifyValidated = (item: RepositoryAccessConfig) =>
  (item.state === "configure" || item.state === "installed") && !!item.appId;

/** Links out to GitHub, in a new tab. */
const GithubLink: React.FC<{ children: ReactNode; href: string }> = ({
  children,
  href,
}) => (
  <div>
    <Button
      href={href}
      icon={<ExportOutlined />}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </Button>
  </div>
);

/** Why the connector's Terraform and shell script aren't shown, if they
 * aren't. */
const RenderProblem: React.FC<{
  rendered: RenderedCommands | undefined;
}> = ({ rendered }) =>
  rendered === undefined ? (
    <Alert
      message={`${CLOUD_MISMATCH} Remove the organization and add it again.`}
      showIcon
      type="error"
    />
  ) : rendered.error ? (
    <Alert message={rendered.error.message} showIcon type="error" />
  ) : null;

type AwsVault = Extract<RepositoryAccessConfig["vault"], { type: "aws-sm" }>;
type GcpVault = Extract<RepositoryAccessConfig["vault"], { type: "gcp-sm" }>;

/** The downloaded key, in the commands that store it. The customer replaces
 * it with the file's path, which the commands quote, as it may have spaces. */
const KEY_FILE = "<file>.pem";

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

const GENERATE_KEY =
  "Once you create the app, GitHub opens its settings page. Under Private keys, click Generate a private key.";

const KEY_STAYS_YOURS = "P0 never reads the key. Only your connector does.";

/** Creates the secret `name` with the downloaded key as its value, in the
 * vault's region, which the AWS CLI would otherwise take from its own
 * configuration. A connector in another account can't use a secret's
 * default key, so such a secret is created with a customer-managed one. */
const toCreateSecretCommand = (
  name: string,
  { secretsRegion }: AwsVault,
  { crossAccount }: { crossAccount: boolean }
) =>
  shellLines(
    "aws secretsmanager create-secret",
    `--name ${name}`,
    ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
    `--secret-string "file://${KEY_FILE}"`,
    `--region ${secretsRegion}`
  );

/** Stores the downloaded key as the value of the secret `name`, which already
 * exists, in the vault's region. */
const toPutSecretValueCommand = (name: string, { secretsRegion }: AwsVault) =>
  shellLines(
    "aws secretsmanager put-secret-value",
    `--secret-id ${name}`,
    `--secret-string "file://${KEY_FILE}"`,
    `--region ${secretsRegion}`
  );

/** Creates the secret `name` with the downloaded key as its value, in the
 * vault's project. A new secret needs a replication policy, and automatic
 * lets Secret Manager choose where it keeps the secret. */
const toCreateGcpSecretCommand = (name: string, { install }: GcpVault) =>
  shellLines(
    "gcloud secrets create",
    name,
    "--replication-policy automatic",
    `--data-file "${KEY_FILE}"`,
    `--project ${install}`
  );

/** Stores the downloaded key as a new version of the secret `name`, which
 * already exists, in the vault's project. */
const toAddGcpSecretVersionCommand = (name: string, { install }: GcpVault) =>
  shellLines(
    "gcloud secrets versions add",
    name,
    `--data-file "${KEY_FILE}"`,
    `--project ${install}`
  );

/** The steps that store the downloaded key: `intro`, then the command that
 * creates the secret, then `ifExists` and the command that stores the key in
 * an existing one. */
const SaveKeySteps: React.FC<{
  create: string;
  ifExists: string;
  intro: React.ReactNode;
  update: string;
}> = ({ create, ifExists, intro, update }) => (
  <Flex gap={8} vertical>
    <Text>
      {GENERATE_KEY} {intro} replacing <Text code>{KEY_FILE}</Text> with the
      file&apos;s path:
    </Text>
    <CommandDisplay commands={create} />
    <Text>{ifExists}</Text>
    <CommandDisplay commands={update} />
    <Text>Then delete the download. {KEY_STAYS_YOURS}</Text>
  </Flex>
);

/** Says how to store the key that GitHub generates as the stored item's
 * secret, in its vault's account and region, or its project. */
const SaveKey: React.FC<{ item: RepositoryAccessConfig }> = ({ item }) => {
  const { hosting, privateKeySecretName: name, vault } = item;
  if (vault.type === "gcp-sm") {
    return (
      <SaveKeySteps
        create={toCreateGcpSecretCommand(name, vault)}
        ifExists="If the secret already exists, store the key as a new version of it instead:"
        intro={
          <>
            Store the downloaded .pem file in {CLOUDS.gcp.vault.label} as the
            secret <Text code>{name}</Text>, in project {vault.install}. With
            the gcloud CLI signed in, run this,
          </>
        }
        update={toAddGcpSecretVersionCommand(name, vault)}
      />
    );
  }
  const crossAccount =
    hosting.type === "aws" && hosting.accountId !== vault.install;
  return (
    <SaveKeySteps
      create={toCreateSecretCommand(name, vault, { crossAccount })}
      ifExists="If the secret already exists, store the key as its value instead:"
      intro={
        <>
          Store the downloaded .pem file in {CLOUDS.aws.vault.label} as the
          secret <Text code>{name}</Text>, in account {vault.install} and region{" "}
          {vault.secretsRegion}.{" "}
          {crossAccount && (
            <>
              The connector runs in another account, so the secret needs a
              customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
              with that key&apos;s ARN.{" "}
            </>
          )}
          With the AWS CLI signed in to account {vault.install}, run this,
        </>
      }
      update={toPutSecretValueCommand(name, vault)}
    />
  );
};

/**
 * A stored item's install steps.
 *
 * On the item's setup page (`stage`) the customer takes every step but the
 * last, with the connector's Terraform and shell script below, then clicks
 * "Next". On the configure page they enter the App ID and click "Finish",
 * which validates the install through the connector. Both show there again,
 * in case that validation sends them back to it.
 */
export const ItemSteps: React.FC<{
  item: RepositoryAccessConfig;
  org: string;
  /** The connector's Terraform and shell script, rendered from the stored
   * item */
  rendered: RenderedCommands | undefined;
}> = ({ item, org, rendered }) => {
  const setUp = item.state === "stage";
  const validated = verifyValidated(item);
  const cloud = CLOUDS[item.hosting.type];
  return (
    <InstallSteps
      org={org}
      size={setUp ? undefined : "small"}
      steps={{
        app: {
          content: (
            <Flex gap={8} vertical>
              <Text>
                Opens GitHub&apos;s form for a new app in {org}, filled in with
                the permissions the connector needs: Administration: write,
                Members: write, Metadata: read, and Custom repository roles:
                read. The app has no webhook, and only {org} can install it.
              </Text>
              <Text>
                App names are unique across GitHub, so GitHub may ask you to
                pick another name. Any name works, and you can rename the app
                later.
              </Text>
              <GithubLink href={toAppCreationUrl(org)}>
                Create the GitHub App
              </GithubLink>
            </Flex>
          ),
          current: setUp,
          done: !setUp,
        },
        key: { content: <SaveKey item={item} />, current: setUp, done: !setUp },
        install: {
          content: (
            <Flex gap={8} vertical>
              <Text>
                On the app&apos;s settings page, click Install App and install
                it on {org}. Only owners of {org} can install it. They pick the
                repositories P0 can grant roles on, and only those repositories
                can be requested. Administration: write is close to repo admin
                on every repo they select, so the repo selection is what limits
                it.
              </Text>
              <GithubLink href={toOrgAppsUrl(org)}>
                Open {org}&apos;s GitHub Apps
              </GithubLink>
            </Flex>
          ),
          current: setUp,
          done: !setUp,
        },
        connector: {
          content: (
            <Flex gap={8} vertical>
              <Text>
                Apply the connector&apos;s Terraform, or run its shell script
                {setUp ? ", below" : ""}. Either deploys the connector as a{" "}
                {cloud.runtime} named {item.hosting.connectorName} that can read
                only the private key&apos;s secret, and lets P0 invoke it.{" "}
                {cloud.deployNote}
              </Text>
              {!setUp && rendered?.terraform !== undefined && (
                <Collapse
                  items={[
                    {
                      children: (
                        <CommandDisplay
                          commands={rendered.terraform}
                          maxRows={20}
                        />
                      ),
                      key: "terraform",
                      label: "Show the connector's Terraform",
                    },
                    {
                      children: (
                        <CommandDisplay
                          commands={rendered.shell}
                          maxRows={20}
                        />
                      ),
                      key: "shell",
                      label: "Show the connector's shell script",
                    },
                  ]}
                />
              )}
              <RenderProblem rendered={rendered} />
            </Flex>
          ),
          current: setUp,
          done: !setUp,
        },
        validate: {
          content: setUp
            ? `Click Next once you've deployed the connector. On the next page, enter the app's ID and click Finish. P0 then checks that GitHub accepts the app's key, that the app is installed on ${org} and isn't suspended, and that it has the permissions above.`
            : validated
              ? "P0 validated the install. Click Finish to install it."
              : `Enter the App ID below and click Finish. P0 checks that GitHub accepts the app's key, that the app is installed on ${org} and isn't suspended, and that it has the permissions above. If a check fails, P0 shows what to fix at the top of this page. ${PROPAGATION_NOTE}`,
          done: validated,
        },
      }}
    />
  );
};
